"use client";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";

const base_url = process.env.REACT_APP_BASE_URL;

const SavedAuctionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) {
      setError("No auction ID provided");
      setLoading(false);
      return;
    }

    const fetchAuctionById = async () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Authentication required. Please log in.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        
        const res = await fetch(`${base_url}/api/auction/getAuctionDetails/${id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const json = await res.json();
        
        if (!res.ok) {
          throw new Error(json.message || `HTTP error! status: ${res.status}`);
        }

        if (json.data) {
          setAuction(json.data);
        } else {
          throw new Error("No auction data found in response");
        }
      } catch (err) {
        console.error("Error fetching auction details:", err);
        setError(err.message || "Failed to fetch auction details");
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionById();
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <section className="container sec_pd">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-dark" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="container sec_pd">
        <div className="mt-5">
          <div className="alert alert-danger" role="alert">
            <h5 className="alert-heading">Error</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-danger btn-sm"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  // No auction found
  if (!auction) {
    return (
      <section className="container sec_pd">
        <div className="mt-5">
          <div className="alert alert-warning" role="alert">
            <h5 className="alert-heading">No Auction Found</h5>
            <p>The requested auction details could not be found.</p>
            <button 
              className="btn btn-outline-warning btn-sm"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Header />
      <section className="container sec_pd">
        <div className="container-fluid py-4">
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button 
              className="btn btn-outline-primary d-flex align-items-center"
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left me-2"></i> Back to List
            </button>
          </div>

          {/* Main Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h2 className="h4 mb-0 text-primary">{auction.title}</h2>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <span className="badge bg-primary">{auction.status}</span>
                {auction.auction_type && (
                  <span className="badge bg-secondary">{auction.auction_type}</span>
                )}
                {auction.approval_status && (
                  <span className={`badge ${
                    auction.approval_status === 'approved' ? 'bg-success' : 
                    auction.approval_status === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'
                  }`}>
                    {auction.approval_status}
                  </span>
                )}
              </div>
              
              {/* Certificates Section */}
              {auction.details?.certificate1 || auction.details?.certificate2 ? (
                <div className="mt-4 pt-4 border-top">
                  <h5 className="mb-3">Certificates</h5>
                  <div className="row">
                    {auction.details.certificate1 && (
                      <div className="col-md-6 mb-3">
                        <strong>Certificate 1:</strong><br />
                        <a 
                          href={auction.details.certificate1} 
                          className="btn btn-dark text-warning mt-2" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          📄 View Certificate 1
                        </a>
                      </div>
                    )}
                    {auction.details.certificate2 && (
                      <div className="col-md-6 mb-3">
                        <strong>Certificate 2:</strong><br />
                        <a 
                          href={auction.details.certificate2} 
                          className="btn btn-dark text-warning mt-2" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          📄 View Certificate 2
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Item Details Section */}
            {auction.details && (
              <div className="card-body">
                <div className="row">
                  {/* Left Column - Basic Info */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Item Details</h5>
                    <dl className="row">
                      {auction.details.description && (
                        <>
                          <dt className="col-sm-4">Description</dt>
                          <dd className="col-sm-8">{auction.details.description}</dd>
                        </>
                      )}
                      {auction.details.artist && (
                        <>
                          <dt className="col-sm-4">Artist</dt>
                          <dd className="col-sm-8">{auction.details.artist}</dd>
                        </>
                      )}
                      {auction.details.item_origin && (
                        <>
                          <dt className="col-sm-4">Origin</dt>
                          <dd className="col-sm-8">{auction.details.item_origin}</dd>
                        </>
                      )}
                      {auction.details.material && (
                        <>
                          <dt className="col-sm-4">Material</dt>
                          <dd className="col-sm-8">{auction.details.material}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                  
                  {/* Right Column - Physical Details */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Physical Details</h5>
                    <dl className="row">
                      {(auction.details.height || auction.details.width || auction.details.depth) && (
                        <>
                          <dt className="col-sm-4">Dimensions</dt>
                          <dd className="col-sm-8">
                            {auction.details.height && `H: ${auction.details.height} `}
                            {auction.details.width && `W: ${auction.details.width} `}
                            {auction.details.depth && `D: ${auction.details.depth}`}
                            {auction.details.measurement_units && ` ${auction.details.measurement_units}`}
                          </dd>
                        </>
                      )}
                      {auction.details.markings && (
                        <>
                          <dt className="col-sm-4">Markings</dt>
                          <dd className="col-sm-8">{auction.details.markings}</dd>
                        </>
                      )}
                      {auction.details.condition && (
                        <>
                          <dt className="col-sm-4">Condition</dt>
                          <dd className="col-sm-8">{auction.details.condition}</dd>
                        </>
                      )}
                    </dl>
                    
                    {/* Price Section */}
                    {(auction.details.price || auction.details.currency) && (
                      <div className="mt-4 pt-3 border-top">
                        <h5 className="mb-3">Pricing</h5>
                        <dl className="row">
                          <dt className="col-sm-4">Price</dt>
                          <dd className="col-sm-8">
                            {auction.details.currency} {auction.details.price || 'Price on request'}
                          </dd>
                        </dl>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Additional Notes */}
                {auction.details.notes && (
                  <div className="mt-4 pt-3 border-top">
                    <h5>Additional Notes</h5>
                    <p className="text-muted">{auction.details.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Images Section */}
          {auction.images?.length > 0 && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">Gallery</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {auction.images
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                    .map((image, index) => (
                      <div key={image.id || index} className="col-md-4 col-6">
                        <div className="position-relative">
                          <img
                            src={image.image}
                            className="img-fluid rounded border shadow-sm"
                            alt={`${image.image_type || 'Auction image'} ${index + 1}`}
                            style={{
                              height: "250px",
                              width: "100%",
                              objectFit: "cover",
                              cursor: "pointer"
                            }}
                            onClick={() => window.open(image.image, '_blank')}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              console.error('Failed to load image:', image.image);
                            }}
                          />
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* No content message */}
          {!auction.details && (!auction.images || auction.images.length === 0) && (
            <div className="alert alert-info">
              <p className="mb-0">No additional details or images available for this auction.</p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default SavedAuctionView;